/**
 * 图片状态管理
 * 管理图片列表、当前图片、上传图片、预览等
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DEFAULT_ZOOM_SCALE, DEFAULT_IMAGE_PREFIX } from '../const/constants.js';

/**
 * @typedef {Object} ImageItem
 * @property {string} url - 图片 URL
 * @property {string} previewUrl - 预览 URL
 * @property {string} OriginUrl - 原始 URL
 * @property {string} type - 图片类型 'url' | 'base64'
 * @property {string} fileType - 文件类型
 * @property {string} prefix - 参数前缀
 * @property {boolean} isExample - 是否为示例图片
 * @property {boolean} cantPreview - 是否禁止预览
 * @property {Array} pdfImgList - PDF 页面图片列表
 * @property {Object} defaultConfig - 默认配置
 */

/**
 * @typedef {Object} RectItem
 * @property {string} key - 矩形框唯一标识
 * @property {string} label - 标签文本
 * @property {boolean} visible - 是否可见
 * @property {Object} position - 位置信息
 */

const initialState = {
  // 图片列表
  imgList: [],           // 示例图片列表
  uploadImgList: [],     // 上传图片列表
  multiUploadPages: [],  // 多图上传批次页列表（用于 multiUpload 模式）
  pdfPreviewPages: [],   // 前端本地渲染出的 PDF 逐页图（不依赖后端压缩包）
  pdfPreviewState: 'idle', // 'idle' | 'loading' | 'done' | 'failed'
  imgListType: 'default', // 'default' | 'upload'
  
  // 当前图片
  currentImage: null,
  
  // 预览相关
  angle: 0,              // 旋转角度
  range: DEFAULT_ZOOM_SCALE, // 缩放比例
  imgPreType: 'preview', // 'preview' | 'col' | 'split'
  showImgList: true,     // 是否显示图片列表
  
  // 矩形框
  rects: [],             // 矩形框列表
  rectDocuments: [],     // 原始矩形框数据
  selectRectKey: '',     // 选中的矩形框 key
  
  // 裁剪
  clipImg: '',           // 裁剪后的图片 base64
  
  // 分页
  totalPage: 1,
  currentPage: 1,
  
  // 图片前缀
  prefix: DEFAULT_IMAGE_PREFIX,
  
  // 状态
  isImageError: false,
};

export const useImageStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========== 图片列表操作 ==========

      /**
       * 设置示例图片列表
       * @param {Array<ImageItem>} list
       */
      setImgList: (list) => set({ imgList: list }, false, 'image/setImgList'),

      /**
       * 设置上传图片列表
       * @param {Array<ImageItem>} list
       */
      setUploadImgList: (list) => set({ uploadImgList: list }, false, 'image/setUploadImgList'),

      /**
       * 添加上传图片
       * @param {ImageItem} img
       * @param {boolean} [append=false] - true 时追加到列表尾部（保持上传先后顺序，
       *   用于多图批次归档）；false 时插入头部（单图模式，最新上传排最前）
       */
      addUploadImage: (img, append = false) => {
        const { uploadImgList } = get();
        // 去重 key：多图批次用唯一 uid（每次上传/归档都是一条独立记录，
        // 即使首图 url 相同也不应互相替换）；单图上传无 uid，沿用 url 去重
        const keyOf = (item) => item?.uid || item?.url;
        const imgKey = keyOf(img);
        const existingIndex = uploadImgList.findIndex(item => keyOf(item) === imgKey);

        if (existingIndex !== -1) {
          // 已存在则原位更新
          const newList = [...uploadImgList];
          newList[existingIndex] = img;
          set({ uploadImgList: newList }, false, 'image/updateUploadImage');
        } else if (append) {
          // 追加到尾部：多图批次归档按上传先后排列，避免后归档的批次插到前面
          set({ uploadImgList: [...uploadImgList, img] }, false, 'image/addUploadImage');
        } else {
          // 不存在则添加到头部
          set({ uploadImgList: [img, ...uploadImgList] }, false, 'image/addUploadImage');
        }
      },

      /**
       * 删除上传图片
       * @param {number} index
       */
      removeUploadImage: (index) => {
        const { uploadImgList } = get();
        const newList = [...uploadImgList];
        newList.splice(index, 1);
        set({ uploadImgList: newList }, false, 'image/removeUploadImage');
      },

      /**
       * 清空上传图片列表
       */
      clearUploadImgList: () => set({ uploadImgList: [] }, false, 'image/clearUploadImgList'),

      // ========== 多图上传批次操作 ==========

      /**
       * 追加多图上传批次页（支持一次追加多页，如批量选择或 PDF 拆页结果）
       * @param {Array<ImageItem>} pages
       */
      addMultiUploadPages: (pages) => {
        if (!pages || pages.length === 0) return;
        const { multiUploadPages } = get();
        set(
          { multiUploadPages: [...multiUploadPages, ...pages] },
          false,
          'image/addMultiUploadPages'
        );
      },

      /**
       * 删除指定索引的多图上传页
       * @param {number} index
       */
      removeMultiUploadPage: (index) => {
        const { multiUploadPages, pdfPreviewPages } = get();
        const newList = [...multiUploadPages];
        const [removed] = newList.splice(index, 1);
        set(
          {
            multiUploadPages: newList,
            // 移除的是 PDF 页时，本地渲染出的页图一并作废
            pdfPreviewPages:
              removed?.fileType === 'pdf' ? [] : pdfPreviewPages,
          },
          false,
          'image/removeMultiUploadPage'
        );
      },

      /**
       * 清空多图上传批次
       */
      clearMultiUploadPages: () =>
        set(
          {
            multiUploadPages: [],
            pdfPreviewPages: [],
            pdfPreviewState: 'idle',
          },
          false,
          'image/clearMultiUploadPages'
        ),

      /**
       * 写入前端本地渲染的 PDF 逐页图
       * @param {Array} pages
       * @param {'idle'|'loading'|'done'|'failed'} [state]
       */
      setPdfPreviewPages: (pages, state = 'done') =>
        set(
          { pdfPreviewPages: pages || [], pdfPreviewState: state },
          false,
          'image/setPdfPreviewPages'
        ),

      /**
       * 设置图片列表类型
       * @param {'default' | 'upload'} type
       */
      setImgListType: (type) => set({ imgListType: type }, false, 'image/setImgListType'),

      // ========== 当前图片操作 ==========

      /**
       * 设置当前图片
       * @param {ImageItem} img
       */
      setCurrentImage: (img) => set({ currentImage: img }, false, 'image/setCurrentImage'),

      /**
       * 更新当前图片属性
       * @param {Partial<ImageItem>} updates
       */
      updateCurrentImage: (updates) => {
        const { currentImage } = get();
        if (currentImage) {
          set({ currentImage: { ...currentImage, ...updates } }, false, 'image/updateCurrentImage');
        }
      },

      // ========== 预览控制 ==========

      /**
       * 设置旋转角度
       * @param {number} angle
       */
      setAngle: (angle) => set({ angle }, false, 'image/setAngle'),

      /**
       * 设置缩放比例
       * @param {number} range
       */
      setRange: (range) => set({ range }, false, 'image/setRange'),

      /**
       * 设置预览类型
       * @param {'preview' | 'col' | 'split'} type
       */
      setImgPreType: (type) => set({ imgPreType: type }, false, 'image/setImgPreType'),

      /**
       * 切换图片列表显示
       * @param {boolean} show
       */
      setShowImgList: (show) => set({ showImgList: show }, false, 'image/setShowImgList'),

      // ========== 矩形框操作 ==========

      /**
       * 设置矩形框列表
       * @param {Array<RectItem>} rects
       */
      setRects: (rects) => set({ rects }, false, 'image/setRects'),

      /**
       * 设置原始矩形框数据（自动转换为可见矩形框）
       * @param {Array} documents
       */
      setRectDocuments: (documents) => {
        const rects = documents.map(r => ({ ...r, visible: true }));
        set({ rectDocuments: documents, rects }, false, 'image/setRectDocuments');
      },

      /**
       * 设置选中的矩形框
       * @param {string} key
       */
      setSelectRectKey: (key) => set({ selectRectKey: key }, false, 'image/setSelectRectKey'),

      /**
       * 切换矩形框可见性
       * @param {string} key
       */
      toggleRectVisible: (key) => {
        const { rects } = get();
        const newRects = rects.map(r => 
          r.key === key ? { ...r, visible: !r.visible } : r
        );
        set({ rects: newRects }, false, 'image/toggleRectVisible');
      },

      // ========== 裁剪操作 ==========

      /**
       * 设置裁剪图片
       * @param {string} clipImg
       */
      setClipImg: (clipImg) => set({ clipImg }, false, 'image/setClipImg'),

      // ========== 分页操作 ==========

      /**
       * 设置总页数
       * @param {number} total
       */
      setTotalPage: (total) => set({ totalPage: total }, false, 'image/setTotalPage'),

      /**
       * 设置当前页码
       * @param {number} page
       */
      setCurrentPage: (page) => set({ currentPage: page }, false, 'image/setCurrentPage'),

      // ========== 其他操作 ==========

      /**
       * 设置图片参数前缀
       * @param {string} prefix
       */
      setPrefix: (prefix) => set({ prefix }, false, 'image/setPrefix'),

      /**
       * 设置图片错误状态
       * @param {boolean} isError
       */
      setIsImageError: (isError) => set({ isImageError: isError }, false, 'image/setIsImageError'),

      /**
       * 清空图片相关状态（切换菜单时调用）
       */
      clearImageState: () => set({
        rects: [],
        rectDocuments: [],
        selectRectKey: '',
        clipImg: '',
        showImgList: true,
        angle: 0,
      }, false, 'image/clearImageState'),

      /**
       * 重置所有状态
       */
      resetImage: () => set(initialState, false, 'image/reset'),
    }),
    { name: 'ImageStore' }
  )
);
